import type { Coordinate } from "../../entities/tracking";

// Coordonnées de repli (centre Cotonou) — utilisées quand une adresse est vide
// ou que le géocodage échoue (réseau, aucun résultat).
export const COTONOU_FALLBACK: Coordinate = { latitude: 6.3703, longitude: 2.3912 };

const NOMINATIM_URL         = "https://nominatim.openstreetmap.org/search";
const NOMINATIM_REVERSE_URL = "https://nominatim.openstreetmap.org/reverse";

// Nominatim exige un User-Agent identifiant l'app sur chaque requête.
const NOMINATIM_HEADERS = {
  Accept:       "application/json",
  "User-Agent": "SOS-MAIN-Mobile/0.1 (support@sosmain.app)",
} as const;

// Cache mémoire (par session JS) des requêtes déjà géocodées — évite de retaper
// Nominatim pour une même adresse pendant un submit (et entre submits).
const cache        = new Map<string, Coordinate | null>();
const reverseCache = new Map<string, string | null>();
const countryCache = new Map<string, string | null>();

function normalize(query: string): string {
  return query.trim().toLowerCase().replace(/\s+/g, " ");
}

// Géocodage léger via Nominatim (OSM) — cohérent avec les tuiles OSM/CARTO de la
// carte de suivi. HTTP pur, aucun module natif → fonctionne aussi en Expo Go.
// Biaisé sur le Bénin (countrycodes=bj) pour des résultats pertinents.
//
// ⚠️ Politique d'usage Nominatim : max 1 req/s, User-Agent requis, pas d'usage
// massif. OK pour le volume MVP (quelques adresses à la création d'une course).
// Pour la prod à fort trafic : héberger une instance Nominatim dédiée OU exposer
// un endpoint de géocodage côté backend et basculer l'URL ici.
export async function geocodeAddress(
  query: string,
  signal?: AbortSignal,
): Promise<Coordinate | null> {
  const q = query.trim();
  if (q.length < 3) return null;

  const key = normalize(q);
  if (cache.has(key)) return cache.get(key) ?? null;

  try {
    const params = new URLSearchParams({
      q,
      format:         "json",
      limit:          "1",
      countrycodes:   "bj",
      addressdetails: "0",
    });
    const res = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
      method:  "GET",
      headers: NOMINATIM_HEADERS,
      signal,
    });
    if (!res.ok) {
      cache.set(key, null);
      return null;
    }
    const json = (await res.json()) as Array<{ lat?: string; lon?: string }>;
    const hit  = json[0];
    if (!hit?.lat || !hit?.lon) {
      cache.set(key, null);
      return null;
    }
    const latitude  = parseFloat(hit.lat);
    const longitude = parseFloat(hit.lon);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      cache.set(key, null);
      return null;
    }
    const coord: Coordinate = { latitude, longitude };
    cache.set(key, coord);
    return coord;
  } catch {
    // Réseau coupé / abort / parse → on laisse l'appelant décider du repli.
    // On NE cache PAS un échec réseau transitoire (pourrait réussir au retry).
    return null;
  }
}

// Une suggestion d'adresse pour l'autocomplete du sélecteur sur carte.
export type AddressSuggestion = {
  id:       string;     // place_id (Google ou Nominatim), ou repli lat,lon
  label:    string;     // version courte pour la ligne de liste
  fullName: string;     // libellé complet (renvoyé à l'appelant à la sélection)
  /**
   * Coordonnées, quand la source les fournit d'emblée — c'est le cas de
   * Nominatim. Google Places ne les livre qu'au Place Details, facturé à part :
   * la suggestion arrive alors SANS `coord`, et l'appelant la résout via
   * `resolvePlaceSuggestion` au moment du choix. D'où l'optionalité.
   */
  coord?:   Coordinate;
  /** Présent uniquement pour les suggestions Google Places. */
  placeId?: string;
};

// Recherche multi-résultats via Nominatim — alimente la liste d'autocomplete.
// Contrairement à `geocodeAddress` (limit=1, usage one-shot), renvoie jusqu'à
// `limit` suggestions. `near` biaise les résultats autour d'un point (viewbox
// non bornée) — utile au Bénin où les noms de rue se répètent d'un quartier à
// l'autre. Renvoie [] en cas d'échec réseau / aucun résultat (jamais throw).
export async function searchAddresses(
  query:   string,
  near?:   Coordinate,
  signal?: AbortSignal,
  limit    = 5,
): Promise<AddressSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) return [];

  try {
    const params = new URLSearchParams({
      q,
      format:         "json",
      limit:          String(limit),
      countrycodes:   "bj",
      addressdetails: "0",
    });
    // Biais de proximité : viewbox ~0.3° (~33 km) autour du centre courant,
    // `bounded=0` → on privilégie sans exclure les résultats hors cadre.
    if (near) {
      const d = 0.3;
      params.set(
        "viewbox",
        `${near.longitude - d},${near.latitude + d},${near.longitude + d},${near.latitude - d}`,
      );
      params.set("bounded", "0");
    }
    const res = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
      method:  "GET",
      headers: NOMINATIM_HEADERS,
      signal,
    });
    if (!res.ok) return [];
    const json = (await res.json()) as Array<{
      place_id?:     number;
      lat?:          string;
      lon?:          string;
      display_name?: string;
    }>;
    return json
      .map((hit): AddressSuggestion | null => {
        const latitude  = parseFloat(hit.lat ?? "");
        const longitude = parseFloat(hit.lon ?? "");
        const fullName  = hit.display_name?.trim();
        if (Number.isNaN(latitude) || Number.isNaN(longitude) || !fullName) {
          return null;
        }
        return {
          id:       String(hit.place_id ?? `${latitude},${longitude}`),
          label:    shortAddress(fullName, 4),
          fullName,
          coord:    { latitude, longitude },
        } satisfies AddressSuggestion;
      })
      .filter((s): s is AddressSuggestion => s !== null);
  } catch {
    return [];
  }
}

// Variante avec repli garanti : retourne toujours une coordonnée (Cotonou par
// défaut) quand la requête est vide ou que le géocodage échoue.
export async function geocodeAddressOrDefault(
  query:    string,
  fallback: Coordinate = COTONOU_FALLBACK,
  signal?:  AbortSignal,
): Promise<Coordinate> {
  const hit = await geocodeAddress(query, signal);
  return hit ?? fallback;
}

// Reverse geocoding : coordonnées → adresse lisible. Cœur du sélecteur sur carte
// (le pin central renvoie un point, on affiche son adresse). Retourne null si
// aucune adresse trouvée ou en cas d'échec réseau.
export async function reverseGeocode(
  coord:   Coordinate,
  signal?: AbortSignal,
): Promise<string | null> {
  // Clé arrondie à ~1 m : évite de retaper Nominatim pour des micro-déplacements.
  const key = `${coord.latitude.toFixed(5)},${coord.longitude.toFixed(5)}`;
  if (reverseCache.has(key)) return reverseCache.get(key) ?? null;

  try {
    const params = new URLSearchParams({
      lat:            String(coord.latitude),
      lon:            String(coord.longitude),
      format:         "json",
      zoom:           "18",
      addressdetails: "0",
    });
    const res = await fetch(`${NOMINATIM_REVERSE_URL}?${params.toString()}`, {
      method:  "GET",
      headers: NOMINATIM_HEADERS,
      signal,
    });
    if (!res.ok) {
      reverseCache.set(key, null);
      return null;
    }
    const json = (await res.json()) as { display_name?: string };
    const name = json.display_name?.trim() || null;
    reverseCache.set(key, name);
    return name;
  } catch {
    return null;
  }
}

/**
 * Pays ISO (deux lettres) correspondant à une position.
 *
 * C'est volontairement une requête distincte du reverse-geocoding d'adresse :
 * le téléphone n'a besoin ni de rue ni de quartier, seulement du pays. Une
 * granularité de 0,1° suffit donc au cache et évite de solliciter Nominatim à
 * chaque mise à jour GPS.
 */
export async function reverseGeocodeCountry(
  coord: Coordinate,
  signal?: AbortSignal,
): Promise<string | null> {
  const key = `${coord.latitude.toFixed(1)},${coord.longitude.toFixed(1)}`;
  if (countryCache.has(key)) return countryCache.get(key) ?? null;

  try {
    const params = new URLSearchParams({
      lat: String(coord.latitude),
      lon: String(coord.longitude),
      format: "json",
      zoom: "3",
      addressdetails: "1",
    });
    const res = await fetch(`${NOMINATIM_REVERSE_URL}?${params.toString()}`, {
      method: "GET",
      headers: NOMINATIM_HEADERS,
      signal,
    });
    if (!res.ok) {
      countryCache.set(key, null);
      return null;
    }
    const json = (await res.json()) as { address?: { country_code?: string } };
    const country = json.address?.country_code?.trim().toUpperCase() || null;
    countryCache.set(key, country);
    return country;
  } catch {
    return null;
  }
}

// Reverse geocoding "ZONE" : coordonnées → nom de quartier/secteur court
// (ex. "Cadjèhoun"), pour un libellé de localisation compact (statut agent…).
// Utilise addressdetails=1 et privilégie le découpage le plus parlant
// (quartier > arrondissement > ville). Retourne null si rien d'exploitable.
const zoneCache = new Map<string, string | null>();

export async function reverseGeocodeZone(
  coord:   Coordinate,
  signal?: AbortSignal,
): Promise<string | null> {
  const key = `${coord.latitude.toFixed(4)},${coord.longitude.toFixed(4)}`;
  if (zoneCache.has(key)) return zoneCache.get(key) ?? null;

  try {
    const params = new URLSearchParams({
      lat:            String(coord.latitude),
      lon:            String(coord.longitude),
      format:         "json",
      zoom:           "16",
      addressdetails: "1",
    });
    const res = await fetch(`${NOMINATIM_REVERSE_URL}?${params.toString()}`, {
      method:  "GET",
      headers: NOMINATIM_HEADERS,
      signal,
    });
    if (!res.ok) {
      zoneCache.set(key, null);
      return null;
    }
    const json = (await res.json()) as {
      address?: Record<string, string | undefined>;
    };
    const a = json.address ?? {};
    // Du plus précis (quartier) au plus large (ville), on prend le premier dispo.
    const zone =
      a.suburb ?? a.neighbourhood ?? a.quarter ?? a.city_district ??
      a.village ?? a.town ?? a.city ?? a.municipality ?? a.county ?? null;
    const clean = zone?.trim() || null;
    zoneCache.set(key, clean);
    return clean;
  } catch {
    return null;
  }
}

// Raccourcit une adresse Nominatim (display_name très long, séparé par des
// virgules) aux `parts` premiers segments — pour un affichage compact dans l'UI.
export function shortAddress(displayName: string, parts = 3): string {
  return displayName
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, parts)
    .join(", ");
}
